import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import { Fragment, useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import {
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowEditStopParams,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowsProp
} from '@mui/x-data-grid-pro';
import { v4 } from 'uuid';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { formatPrice, getDate, getFormattedDate, getUTCRunDate, isObjectEmpty } from '../../../utils/helpers';
import { createETU, ETU, updateETU } from '../../../store/ETU';
import { PageCard } from '../CardWithTitle';
import { Icon } from '../../Atoms/Icon';
import { DEFAULT_MINI_TABLE_SX_STYLES } from '../../../utils/styleHelpers';
import { isData, isObserver } from '../../../store/User';
import { CustomMoneyInput } from './CustomMoneyInput';
import { CustomDateInput } from './CustomDateInput';
import { TextSpan } from '../../Atoms/Text';
export interface ETUMiniTableParams {
  id: string;
  fetchFunction: () => Promise<void>;
  removeFunction?: (id: string, drawer?: boolean) => Promise<void>;
  dataSubject: () => BehaviorSubject<ETU[]>;
  loadingSubject: () => BehaviorSubject<boolean>;
  showCreate?: boolean;
  drawer?: boolean;
}

export const ETUMiniTable = ({
  id,
  fetchFunction,
  removeFunction,
  dataSubject,
  loadingSubject,
  showCreate = true,
  drawer = false
}: ETUMiniTableParams) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<GridRowsProp>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  const handleRemove = async (id: GridRowId) => {
    if (removeFunction) {
      removeFunction(id as string, drawer);
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit, fieldToFocus: 'netBilled' } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    setRows((rows) => rows.filter((row) => row.id !== id));
    handleRemove(id);
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    });

    const editedRow = rows.find((row) => (row as ETU).id === id);
    if (editedRow?.isNew) {
      setRows((rows) => rows.filter((row) => (row as ETU).id !== id));
    }
  };

  const handleAddClick = () => {
    const newTicketId = v4();
    const latestRow = rows[rows.length - 1];
    const newBillingDate = latestRow?.billingMonth ? getUTCRunDate(latestRow.billingMonth, 1).toISOString() : '';
    const newRunDate = latestRow?.commissionRun ? getUTCRunDate(latestRow.commissionRun).toISOString() : '';

    setRows((oldRows) => [
      ...oldRows,
      {
        id: newTicketId,
        netBilled: latestRow?.netBilled ?? 0,
        grossCommission: latestRow?.grossCommission ?? 0,
        billingMonth: newBillingDate,
        commissionRun: newRunDate,
        ticket: id,
        receivedAsExpected: false,
        isNew: true
      }
    ]);

    setRowModesModel((oldModel) => ({
      ...oldModel,
      [newTicketId]: { mode: GridRowModes.Edit, fieldToFocus: 'netBilled' }
    }));
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const index = rows.findIndex((row) => row.id === newRow.id);
    if (index === -1) return newRow;

    const updatedRow = { ...newRow, isNew: !!newRow.isNew, isCreating: !!newRow.isNew };
    setRows((rows) => rows.map((row) => (row.id === newRow.id ? updatedRow : row)));

    // Preform Api Update
    const etuUpdate = JSON.parse(JSON.stringify(updatedRow));
    if (etuUpdate.isNew) delete etuUpdate.id;
    delete etuUpdate.isNew;
    delete etuUpdate.isCreating;

    if (etuUpdate.id) updateETU(etuUpdate as ETU, drawer);
    else createETU(etuUpdate as ETU, drawer);
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleRowEditStop = (params: GridRowEditStopParams, event: unknown) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (event as any).defaultMuiPrevented = true;
    }
  };

  useEffect(() => {
    const dataSub = dataSubject().subscribe((r) => {
      setRows((rows) => {
        const newRows = rows.filter((row) => row.isNew && !row.isCreating);
        return [...newRows, ...r];
      });
    });
    const assetLoadingSub = loadingSubject().subscribe((l) => setLoading(l));

    fetchFunction();

    return () => {
      if (dataSub) dataSub.unsubscribe();
      if (assetLoadingSub) assetLoadingSub.unsubscribe();
    };
  }, []);

  const tableCols: GridColDef[] = [
    {
      headerName: 'Net Billed',
      field: 'netBilled',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      type: 'number',
      sortable: true,
      editable: true,
      valueFormatter: (value) => formatPrice(value),
      renderEditCell: (params: GridRenderCellParams) => <CustomMoneyInput {...params} />
    },
    {
      headerName: 'Gross Commission',
      field: 'grossCommission',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      type: 'number',
      sortable: true,
      editable: true,
      valueFormatter: (value) => formatPrice(value),
      renderEditCell: (params: GridRenderCellParams) => <CustomMoneyInput {...params} />
    },
    {
      headerName: 'Billing Month',
      field: 'billingMonth',
      flex: 1,
      type: 'date',
      sortable: true,
      editable: true,
      valueFormatter: (value) => {
        return getFormattedDate(value, 'MMM YYYY');
      },
      sortComparator: (v1, v2) => {
        return getDate(v1).getTime() - getDate(v2).getTime();
      },
      renderEditCell: (params: GridRenderCellParams) => (
        <CustomDateInput
          {...params}
          isMonthYear
        />
      )
    },
    {
      headerName: 'Expected Run',
      field: 'commissionRun',
      flex: 1,
      type: 'date',
      sortable: true,
      editable: true,
      valueFormatter: (value) => {
        return getFormattedDate(value, 'MMM YYYY');
      },
      sortComparator: (v1, v2) => {
        return getDate(v1).getTime() - getDate(v2).getTime();
      },
      renderEditCell: (params: GridRenderCellParams) => (
        <CustomDateInput
          {...params}
          isMonthYear
        />
      )
    },
    {
      field: 'receivedAsExpected',
      type: 'boolean',
      headerName: 'Received',
      editable: true,
      sortable: false,
      filterable: false
    }
  ];

  if (!isObserver() && !isData()) {
    tableCols.push({
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key="save"
              icon={<SaveIcon sx={{ color: 'primary.main' }} />}
              label="Save"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              key="cancel"
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />
          ];
        }

        return [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon color="buttonGrey" />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon color="buttonGrey" />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />
        ];
      }
    });
  }

  const cardActions = () => {
    return isObserver() || isData() || !showCreate ? (
      <></>
    ) : (
      <button
        className="flex items-center space-x-2"
        disabled={!isObjectEmpty(rowModesModel)}
        onClick={handleAddClick}
        data-cy="new-etu-button"
        data-testid="new-etu-button">
        <Icon type="plus" />
        <TextSpan
          size="sm14"
          color={!isObjectEmpty(rowModesModel) ? 'grey5' : 'indigo'}>
          New Expected True-Up
        </TextSpan>
      </button>
    );
  };

  return (
    <div
      className="w-full"
      data-testid="etu-mini-table-container">
      <PageCard
        title={`Expected True-Ups (${rows.length})`}
        cardActions={cardActions()}>
        <Fragment>
          {loading && <ComponentSpinner label="Loading ETU Data" />}
          {!loading && (
            <DataGridPro
              rows={rows}
              sx={{
                ...DEFAULT_MINI_TABLE_SX_STYLES,
                maxHeight: '30rem',
                overflow: 'auto',
                border: 'unset'
              }}
              rowHeight={40}
              columnHeaderHeight={40}
              showCellVerticalBorder
              hideFooter
              showColumnVerticalBorder
              initialState={{
                sorting: {
                  sortModel: [{ field: 'billingMonth', sort: 'desc' }]
                }
              }}
              columns={tableCols}
              editMode={!isObserver() && !isData() ? 'row' : undefined}
              rowModesModel={rowModesModel}
              onRowModesModelChange={handleRowModesModelChange}
              processRowUpdate={processRowUpdate}
              onRowEditStop={handleRowEditStop}
            />
          )}
        </Fragment>
      </PageCard>
    </div>
  );
};
